'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Trophy, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { generateKnockoutStage } from '@/api/tournament/tournamentApi';
import { handleGlobalError } from '@/utils/error-handler';

interface GenerateKnockoutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tournamentId: string;
  onSuccess: () => void;
}

export function GenerateKnockoutModal({
  open,
  onOpenChange,
  tournamentId,
  onSuccess,
}: GenerateKnockoutModalProps) {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateKnockoutStage = async () => {
    try {
      setIsGenerating(true);

      // Use the service method to generate the knockout phase
      await generateKnockoutStage(tournamentId);

      toast({
        title: 'Fase de eliminatorias generada',
        description: 'La fase de eliminatorias ha sido generada correctamente.',
        variant: 'default',
      });

      // Close the modal and notify the parent component
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error al generar la fase de eliminatorias:', error);
      handleGlobalError(
        error,
        'Error',
        'No se pudo generar la fase de eliminatorias. Inténtalo de nuevo.',
      );
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Generar fase de eliminatorias
          </DialogTitle>
          <DialogDescription>
            Has completado todos los partidos de la fase de grupos. ¿Deseas
            generar la fase de eliminatorias ahora?
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="rounded-lg bg-yellow-50 p-4 text-sm text-yellow-800">
            <p className="font-medium">Importante:</p>
            <ul className="mt-2 list-inside list-disc space-y-1">
              <li>
                Una vez generada la fase de eliminatorias, no podrás modificar
                los resultados de la fase de grupos.
              </li>
              <li>
                Los equipos clasificados pasarán automáticamente a la fase de
                eliminatorias según su posición en los grupos.
              </li>
              <li>Este proceso no se puede deshacer.</li>
            </ul>
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isGenerating}
          >
            Cancelar
          </Button>
          <Button onClick={handleGenerateKnockoutStage} disabled={isGenerating}>
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generando...
              </>
            ) : (
              'Generar fase de eliminatorias'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
